import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './faq.css';

function FAQ() {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      question: "What services does Ceylon Eye Care provide?",
      answer: "We provide comprehensive eye care services including vision testing, contact lens fittings, treatment for eye diseases, pediatric optometry, and low vision rehabilitation. Our team of certified optometrists offers personalized care using state-of-the-art equipment."
    },
    {
      question: "How often should I get my eyes examined?",
      answer: "Adults should have a comprehensive eye exam every 1-2 years. Children should have their first eye exam at 6 months, then at age 3, before first grade, and every 2 years thereafter. Seniors (65+) and those with existing eye conditions may need annual exams."
    },
    {
      question: "What's the difference between an optometrist and ophthalmologist?",
      answer: "Optometrists (ODs) are primary eye care providers who examine, diagnose, treat and manage eye diseases and disorders. Ophthalmologists are medical doctors (MDs) who specialize in eye surgery and complex eye conditions. We work closely with ophthalmologists when surgical intervention is needed."
    },
    {
      question: "Do you accept insurance plans?",
      answer: "Yes, we accept most major insurance plans including VHI, Medicare, and leading private insurers. Please contact our office with your insurance details and we'll verify your coverage before your appointment. We also offer affordable self-pay options."
    },
    {
      question: "How long does an eye exam typically take?",
      answer: "A comprehensive eye exam usually takes about 45-60 minutes. This includes preliminary tests, discussion of your visual needs, the examination itself, and time to discuss findings and treatment options. Specialized tests or contact lens fittings may require additional time."
    },
    {
      question: "What should I bring to my eye exam?",
      answer: "Please bring your current glasses or contacts, a list of medications, your insurance card, and any relevant medical records. If it's your first visit, arrive 15 minutes early to complete paperwork or download forms from our website."
    },
    {
      question: "Can eye exams detect other health problems?",
      answer: "Yes, eye exams can reveal signs of systemic conditions like diabetes, high blood pressure, high cholesterol, and even some neurological disorders. Our optometrists are trained to identify these signs and will refer you to appropriate specialists when necessary."
    },
    {
      question: "What COVID-19 safety measures are in place?",
      answer: "We follow strict sanitation protocols, require masks for all patients and staff, perform temperature checks, and have implemented social distancing in our waiting areas. We also offer telehealth consultations for certain services when appropriate."
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <motion.div 
      className="faq-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="faq-header">
        <motion.h1
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Frequently Asked Questions
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Ceylon Eye Care Optometry Association
        </motion.p>
      </div>

      <div className="faq-content">
        <div className="faq-image">
          <motion.img
            src="/faq.jpeg" // Replace with your image path
            alt="Eye care professional"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          />
        </div>
        
        <div className="faq-accordion">
          {faqs.map((faq, index) => (
            <div 
              className={`faq-item ${activeIndex === index ? 'active' : ''}`}
              key={index}
            >
              <motion.div
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                whileHover={{ backgroundColor: '#f5f5f5' }}
              >
                <h3>{faq.question}</h3>
                <motion.div
                  animate={{ rotate: activeIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="#3F51B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    className="faq-answer"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      <motion.div 
        className="faq-contact"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <h2>Still have questions?</h2>
        <p>Contact our patient care team for more information</p>
        <div className="contact-methods">
          <a href="tel:+94112345678" className="contact-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22 20.47 21.55 20.92 21 20.92C12.61 20.92 3.08 11.39 3.08 3C3.08 2.45 3.53 2 4.08 2H7.08C7.63 2 8.08 2.45 8.08 3C8.08 5.85 8.71 8.64 9.91 11.19C10.09 11.57 10.03 12.02 9.75 12.34L7.13 15.35C8.91 18.06 11.59 20.41 14.66 21.25L17.67 18.63C17.99 18.35 18.44 18.3 18.82 18.47C21.36 19.68 24.15 20.31 27 20.31C27.55 20.31 28 20.76 28 21.31V24.31C28 24.86 27.55 25.31 27 25.31C18.61 25.31 7.69 14.39 7.69 6C7.69 5.45 8.14 5 8.69 5H11.69C12.24 5 12.69 5.45 12.69 6C12.69 8.24 13.37 10.45 14.67 12.38C14.94 12.79 14.91 13.32 14.59 13.7L12.31 16.38C14.05 18.58 16.42 20.25 19.13 20.96C19.5 21.05 19.85 20.89 20.04 20.57L21.72 17.82C21.89 17.53 21.89 17.18 21.72 16.89C21.56 16.63 21.28 16.48 21 16.48L17 16.48C16.45 16.48 16 16.93 16 17.48C16 18.92 16.32 20.34 16.93 21.65C16.63 21.6 16.33 21.54 16.04 21.47L13.35 20.81C11.8 20.41 10.37 19.65 9.15 18.59C7.93 17.53 6.96 16.21 6.32 14.72L5.54 12.96C5.46 12.66 5.4 12.36 5.35 12.06C6.66 12.67 8.08 13 9.52 13C10.07 13 10.52 12.55 10.52 12V8.03C10.52 7.75 10.37 7.48 10.11 7.33C9.84 7.17 9.52 7.16 9.24 7.29L6.5 8.97C6.18 9.13 5.99 9.46 6 9.82C6.06 12.28 7.18 14.6 8.99 16.4C10.8 18.21 13.12 19.33 15.58 19.39C15.94 19.4 16.27 19.21 16.43 18.89L18.11 16.14C18.24 15.86 18.23 15.54 18.07 15.27C17.92 15.01 17.65 14.86 17.37 14.86H13.37C12.82 14.86 12.37 15.31 12.37 15.86Z" fill="#3F51B5"/>
            </svg>
            +94 111 700 903
          </a>
          <a href="mailto:info@ceyloneyecare.lk" className="contact-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#3F51B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="#3F51B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            info@optometryassociation.lk
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default FAQ;